import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AiQuestionCard from 'app/shared/components/AiQuestionCard';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  answerStepQuestionAction,
  loadStepPendingQuestionAction,
} from 'store/aiProcessRuns/actions';
import {
  selectHasStepPendingQuestionEntry,
  selectIsAnsweringStep,
  selectStepPendingQuestion,
} from 'store/aiProcessRuns/selectors';

const StepQuestionActions = ({ stepExecutionId }) => {
  const dispatch = useDispatch();
  const pendingQuestion = useSelector(selectStepPendingQuestion(stepExecutionId));
  const hasEntry = useSelector(selectHasStepPendingQuestionEntry(stepExecutionId));
  const isAnswering = useSelector(selectIsAnsweringStep(stepExecutionId));

  const onSubmit = useCallback(
    answersJson => {
      dispatch(answerStepQuestionAction({ stepExecutionId, answersJson, dismissed: false }));
    },
    [dispatch, stepExecutionId],
  );

  const onDismiss = useCallback(() => {
    dispatch(answerStepQuestionAction({ stepExecutionId, answersJson: null, dismissed: true }));
  }, [dispatch, stepExecutionId]);

  // Reload path: a page refresh loses the live SignalR payload, so fetch the
  // pending questions over REST when the gate is shown and the slice has no
  // entry yet (the live path otherwise wins and short-circuits this).
  useEffectSafeStrictMode(() => {
    if (!hasEntry && stepExecutionId) {
      dispatch(loadStepPendingQuestionAction({ stepExecutionId }));
    }
  }, [dispatch, hasEntry, stepExecutionId]);

  const questions = pendingQuestion?.questions ?? null;

  if (_.isNil(questions) || questions.length === 0) {
    return null;
  }

  return (
    <AiQuestionCard
      isSubmitting={isAnswering}
      questions={questions}
      onDismiss={onDismiss}
      onSubmit={onSubmit}
    />
  );
};

StepQuestionActions.propTypes = {
  stepExecutionId: PropTypes.string.isRequired,
};

export default memo(StepQuestionActions);
